"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Search, Check, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { createClient } from "@/lib/supabase/client"

interface UnsplashImage {
  id: string
  urls: {
    regular: string
    small: string
    thumb: string
  }
  user: {
    name: string
    username: string
    links: {
      html: string
    }
  }
  links: {
    html: string
    download_location: string
  }
  description?: string
  alt_description?: string
}

// Database image interface (from spot_images table)
interface DatabaseImage {
  id: string
  unsplash_id: string
  unsplash_url: string
  unsplash_thumb_url: string
  photographer_name: string
  photographer_username: string
  photographer_url: string
  votes: number
  user_voted?: boolean
}

// Combined image interface for display
interface DisplayImage {
  id: string // Database ID or Unsplash ID
  unsplashId: string
  url: string
  thumbUrl: string
  photographerName: string
  photographerUsername: string
  photographerUrl: string
  downloadLocation?: string
  votes: number
  userVoted: boolean
  isFromDatabase: boolean // true if from database, false if from Unsplash search
}

interface UnsplashImagePickerProps {
  locationName: string
  spotId?: string // Spot ID to fetch existing images and enable voting
  currentUserId?: string // Current user ID for voting
  onImageSelect: (image: {
    unsplashId: string
    url: string
    thumbUrl: string
    photographerName: string
    photographerUsername: string
    photographerUrl: string
    downloadLocation: string
  }) => void
  selectedImageId?: string
  refreshTrigger?: number // Trigger refresh when this value changes
}

export function UnsplashImagePicker({ locationName, spotId, currentUserId, onImageSelect, selectedImageId, refreshTrigger }: UnsplashImagePickerProps) {
  const [displayImages, setDisplayImages] = useState<DisplayImage[]>([])
  const [unsplashImages, setUnsplashImages] = useState<UnsplashImage[]>([])
  const [databaseImages, setDatabaseImages] = useState<DatabaseImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState(locationName)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const IMAGES_PER_PAGE = 5

  const UTM_PARAMS = "utm_source=surf_spots&utm_medium=referral"

  // Fetch existing images from database when spotId is provided
  useEffect(() => {
    if (spotId) {
      loadDatabaseImages()
    }
  }, [spotId, currentUserId, refreshTrigger]) // Refresh when refreshTrigger changes

  // Merge database images with Unsplash results and sort by votes
  useEffect(() => {
    const combined: DisplayImage[] = []

    // Add database images first (already sorted by votes from API)
    databaseImages.forEach((dbImg) => {
      combined.push({
        id: dbImg.id,
        unsplashId: dbImg.unsplash_id,
        url: dbImg.unsplash_url,
        thumbUrl: dbImg.unsplash_thumb_url,
        photographerName: dbImg.photographer_name,
        photographerUsername: dbImg.photographer_username,
        photographerUrl: dbImg.photographer_url,
        votes: dbImg.votes || 0,
        userVoted: dbImg.user_voted || false,
        isFromDatabase: true,
      })
    })

    // Add Unsplash images that aren't already in database
    const existingUnsplashIds = new Set(databaseImages.map((img) => img.unsplash_id))
    unsplashImages.forEach((unsplashImg) => {
      if (!existingUnsplashIds.has(unsplashImg.id)) {
        combined.push({
          id: unsplashImg.id,
          unsplashId: unsplashImg.id,
          url: unsplashImg.urls.regular,
          thumbUrl: unsplashImg.urls.thumb,
          photographerName: unsplashImg.user.name,
          photographerUsername: unsplashImg.user.username,
          photographerUrl: `${unsplashImg.user.links.html}?${UTM_PARAMS}`,
          downloadLocation: unsplashImg.links.download_location,
          votes: 0,
          userVoted: false,
          isFromDatabase: false,
        })
      }
    })

    // Sort by votes (descending), then by whether it's from database (database first)
    combined.sort((a, b) => {
      if (b.votes !== a.votes) {
        return b.votes - a.votes
      }
      // If votes are equal, prioritize database images
      if (a.isFromDatabase && !b.isFromDatabase) return -1
      if (!a.isFromDatabase && b.isFromDatabase) return 1
      return 0
    })

    setDisplayImages(combined)
  }, [databaseImages, unsplashImages])

  useEffect(() => {
    if (locationName) {
      setSearchQuery(locationName)
      searchImages(locationName, 1)
    }
  }, [locationName])

  const loadDatabaseImages = async () => {
    if (!spotId) return

    try {
      const response = await fetch(`/api/spot-images?spotId=${spotId}`)
      if (!response.ok) {
        throw new Error("Failed to fetch existing images")
      }

      const data = await response.json()
      if (data.success && data.images) {
        setDatabaseImages(data.images)
      }
    } catch (err) {
      console.error("Error loading database images:", err)
      // Don't show error to user, just continue without existing images
    }
  }


  const searchImages = async (query: string, page = 1) => {
    if (!query.trim()) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `/api/unsplash/search?query=${encodeURIComponent(query)}&per_page=${IMAGES_PER_PAGE}&page=${page}`,
      )

      if (!response.ok) {
        throw new Error("Failed to fetch images from Unsplash")
      }

      const data = await response.json()
      const newUnsplashImages = data.results || []
      setUnsplashImages(newUnsplashImages)
      setTotalPages(data.total_pages || 1)
      setCurrentPage(page)

      // Only show error if no Unsplash results AND no database images
      if (newUnsplashImages.length === 0 && databaseImages.length === 0) {
        setError("No images found. Try a different search term.")
      } else {
        setError(null)
      }
    } catch (err) {
      console.error("Error fetching Unsplash images:", err)
      setError("Failed to load images. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    setCurrentPage(1)
    searchImages(searchQuery, 1)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      searchImages(searchQuery, currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      searchImages(searchQuery, currentPage + 1)
    }
  }

  const handleImageSelect = async (image: DisplayImage) => {
    // Track download for Unsplash images
    if (!image.isFromDatabase && image.downloadLocation) {
      try {
        await fetch("/api/unsplash/download", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ downloadLocation: image.downloadLocation }),
        })
      } catch (err) {
        console.error("Error tracking download:", err)
      }
    }

    onImageSelect({
      unsplashId: image.unsplashId,
      url: image.url,
      thumbUrl: image.thumbUrl,
      photographerName: image.photographerName,
      photographerUsername: image.photographerUsername,
      photographerUrl: image.photographerUrl,
      downloadLocation: image.downloadLocation || "",
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="image-search" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block mb-2">
          Choose a Photo from Unsplash
        </Label>
        <div className="flex gap-2">
          <Input
            id="image-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleSearch(e as any)
              }
            }}
            placeholder="Search for location photos..."
            className="flex-1 h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 shadow-sm"
          />
          <Button 
            type="button" 
            variant="outline" 
            disabled={loading}
            className="h-12 w-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
            onClick={(e) => {
              e.preventDefault()
              handleSearch(e as any)
            }}
          >
            <Search className="w-4 h-4 text-blue-500" />
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="text-center py-8 text-muted-foreground">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
          <p>Searching Unsplash...</p>
        </div>
      )}

      {!loading && displayImages.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-foreground">Select the photo that best represents this spot:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {displayImages.map((image) => (
              <div
                key={image.id}
                className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                  selectedImageId === image.unsplashId
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : "border-transparent hover:border-blue-300"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleImageSelect(image)}
                  className="w-full h-full absolute inset-0 z-0 cursor-pointer"
                  aria-label={`Select photo by ${image.photographerName}`}
                  style={{ pointerEvents: 'auto' }}
                />
                <img
                  src={image.thumbUrl || "/placeholder.svg"}
                  alt={`Photo by ${image.photographerName}`}
                  className="w-full h-32 object-cover relative z-0 pointer-events-none"
                />
                {selectedImageId === image.unsplashId && (
                  <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center z-10 pointer-events-none">
                    <div className="bg-blue-500 text-white rounded-full p-2">
                      <Check className="w-5 h-5" />
                    </div>
                  </div>
                )}
                {/* Vote count badge - always show for database images */}
                {image.isFromDatabase && (
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm z-30 pointer-events-none">
                    {image.votes || 0} {image.votes === 1 ? "vote" : "votes"}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                  <p className="text-white text-xs truncate">Photo by {image.photographerName}</p>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Photos from{" "}
            <a
              href={`https://unsplash.com?${UTM_PARAMS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground transition-colors"
            >
              Unsplash
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
